import { Box, Stack, type SxProps, type Theme } from "@mui/material";
import type { ConnectedApp } from "@/api/oauth";
import { matchKnownApp, type KnownApp } from "@/components/oauth/knownApps";
import { AppTile, CounterTile, WebshareTile } from "./AgentTile";

/** How many connected applications get a tile of their own; the rest are counted. */
const MAX_APP_TILES = 3;

/**
 * The clients the not-connected visual shows, left to right, with the Webshare
 * tile in the middle. A picture of what can connect, not a list of what has.
 */
const SHOWCASE_LEFT: KnownApp[] = ["cursor", "claude"];
const SHOWCASE_RIGHT: KnownApp[] = ["gemini-cli", "codex"];

/**
 * The dotted line that joins the tiles. An SVG rather than a dotted border so
 * the dots sit every 4px as the design draws them, which `border-style:
 * dotted` does not allow.
 */
function DottedLine({ sx }: { sx?: SxProps<Theme> }) {
  return (
    <Box
      component="svg"
      aria-hidden
      sx={[
        { display: "block", height: 1, color: "action.disabled" },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* Centred on the one pixel row, so the dots print crisp rather than
          smeared over two. */}
      <line
        x1="0"
        y1="0.5"
        x2="100%"
        y2="0.5"
        stroke="currentColor"
        strokeWidth={1}
        strokeDasharray="1 3"
      />
    </Box>
  );
}

/** Five client tiles threaded on one dotted line, Webshare in the middle. */
function NotConnectedVisual() {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2.25}
      useFlexGap
      sx={{ position: "relative" }}
      data-testid="ai-agents-visual-default"
    >
      <DottedLine
        sx={{
          position: "absolute",
          left: 0,
          width: "100%",
          top: "50%",
          mt: "-0.5px",
        }}
      />
      {SHOWCASE_LEFT.map((app) => (
        <AppTile key={app} app={app} size="small" />
      ))}
      <WebshareTile />
      {SHOWCASE_RIGHT.map((app) => (
        <AppTile key={app} app={app} size="small" />
      ))}
    </Stack>
  );
}

/**
 * One tile per connected application, in the order the API returns them, up
 * to three; from four on, three tiles and a "+N" counter. Then the line to the
 * Webshare tile. Only our own marks are drawn, matched on the application's
 * name, and never its name.
 */
function ConnectedVisual({ apps }: { apps: ConnectedApp[] }) {
  const shown = apps.slice(0, MAX_APP_TILES);
  const rest = apps.length - shown.length;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      useFlexGap
      data-testid="ai-agents-visual-connected"
    >
      {shown.map((app) => (
        <AppTile
          key={app.client_id}
          app={matchKnownApp(app.name)}
          size="large"
        />
      ))}
      {rest > 0 && <CounterTile count={rest} />}
      <DottedLine sx={{ width: 12, flexShrink: 0 }} />
      <WebshareTile />
    </Stack>
  );
}

/** The right-hand visual of the dashboard's AI agent banner. */
export default function AiAgentsVisual({
  apps,
}: {
  /** The connected applications; none or an empty list is the not-connected state. */
  apps: ConnectedApp[] | undefined;
}) {
  return apps?.length ? (
    <ConnectedVisual apps={apps} />
  ) : (
    <NotConnectedVisual />
  );
}
